import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Layout from '../components/Layout';

const CreateAssociation: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');

    try {
      const response = await api.uploadImage(file);
      if (response.url) {
        setImageUrl(response.url);
      }
    } catch (err: any) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.createAssociation({
        name,
        image: imageUrl,
      });

      if (response.success) {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create association');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 border border-heynay-border rounded-2xl text-heynay-dark-gray font-medium hover:bg-heynay-light-gray transition-colors"
        >
          ← Back to Associations
        </button>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="bg-white border border-heynay-border rounded-2xl p-8">
          <h3 className="text-3xl font-bold text-heynay-dark-gray mb-2">Create New Association</h3>
          <p className="text-heynay-gray mb-6">Add a new association to the Heynay platform</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Name */}
            <div className="mb-6">
              <label htmlFor="name" className="block text-heynay-dark-gray font-semibold mb-2">
                Association Name *
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter association name"
                required
                disabled={loading}
                className="w-full border border-heynay-border rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-heynay-blue focus:border-transparent disabled:opacity-50"
              />
            </div>

            {/* Image Upload */}
            <div className="mb-6">
              <label htmlFor="image" className="block text-heynay-dark-gray font-semibold mb-2">
                Association Logo/Image
              </label>
              <input
                type="file"
                id="image"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading || loading}
                className="w-full border border-heynay-border rounded-2xl px-5 py-3 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-heynay-blue file:text-white hover:file:opacity-90 disabled:opacity-50"
              />
              {uploading && (
                <div className="mt-3 flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-heynay-blue border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-heynay-gray text-sm">Uploading image...</span>
                </div>
              )}
              {imageUrl && !uploading && (
                <div className="mt-4 text-center">
                  <img
                    src={imageUrl}
                    alt="Preview"
                    className="max-w-full max-h-48 rounded-xl mx-auto"
                  />
                </div>
              )}
            </div>

            {/* Image URL */}
            <div className="mb-8">
              <label htmlFor="imageUrl" className="block text-heynay-dark-gray font-semibold mb-2">
                Or enter image URL
              </label>
              <input
                type="url"
                id="imageUrl"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                disabled={loading}
                className="w-full border border-heynay-border rounded-2xl px-5 py-3 focus:outline-none focus:ring-2 focus:ring-heynay-blue focus:border-transparent disabled:opacity-50"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading || uploading}
                className="flex-1 bg-heynay-blue text-white font-bold py-3 px-6 rounded-3xl hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Association'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                disabled={loading || uploading}
                className="px-8 py-3 border border-heynay-border rounded-3xl text-heynay-dark-gray font-bold hover:bg-heynay-light-gray transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CreateAssociation;
